import type { GarmentImageJob, GarmentImageJobResponse } from '../../shared/imageProcessing';
import { readError } from './api';

async function request(garmentId: string, init?: RequestInit): Promise<GarmentImageJob | null> {
  const response = await fetch(`/api/items/${encodeURIComponent(garmentId)}/processing`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...init?.headers,
    },
    ...init,
  });
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as GarmentImageJobResponse).job;
}

export function getGarmentImageJob(garmentId: string): Promise<GarmentImageJob | null> {
  return request(garmentId);
}

export function startGarmentImageJob(
  garmentId: string,
  requestKey: string,
  kind: GarmentImageJob['kind'],
  options?: { autoApply?: boolean },
): Promise<GarmentImageJob | null> {
  return request(garmentId, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Processing-Key': requestKey,
    },
    body: JSON.stringify({
      action: kind === 'studio-photo' ? 'start-studio' : 'start-background',
      ...(options?.autoApply ? { autoApply: true } : {}),
    }),
  });
}

export function actOnGarmentImageJob(
  garmentId: string,
  action: 'retry' | 'cancel' | 'restore' | 'accept-studio' | 'discard-studio',
): Promise<GarmentImageJob | null> {
  return request(garmentId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
}

async function processedThumbnail(photoUrl: string): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return null;
  try {
    const response = await fetch(photoUrl, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) return null;
    const image = await createImageBitmap(await response.blob());
    const maximum = 720;
    const ratio = Math.min(1, maximum / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * ratio));
    canvas.height = Math.max(1, Math.round(image.height * ratio));
    canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close();
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.84));
  } catch {
    return null;
  }
}

export async function saveProcessedThumbnail(
  garmentId: string,
  job: GarmentImageJob,
  photoUrl: string,
): Promise<boolean> {
  const thumbnail = await processedThumbnail(
    `${photoUrl}${photoUrl.includes('?') ? '&' : '?'}processed=${job.updatedAt}`,
  );
  if (!thumbnail) return false;
  const form = new FormData();
  form.set('jobId', job.id);
  form.set('thumbnail', thumbnail, 'processed-thumbnail.webp');
  const response = await fetch(`/api/items/${encodeURIComponent(garmentId)}/processing/thumbnail`, {
    method: 'PUT',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    body: form,
  });
  if (!response.ok) throw new Error(await readError(response));
  return true;
}
